import React, { useState, useEffect } from 'react';
import { 
  Clock, AlertTriangle, CheckCircle, XCircle, 
  ChevronDown, Search, Filter, Calendar, ArrowUpRight 
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

const GBApprovalDashboard = () => {
  const [pendingRisks, setPendingRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [expandedRisk, setExpandedRisk] = useState(null);
  const [comment, setComment] = useState('');
  const [stats, setStats] = useState({
    pendingCount: 0,
    nextMeeting: null,
    strategicCount: 0,
    financialImpact: 0,
    approvalStats: {
      total: 0,
      approved: 0,
      rejected: 0
    }
  });

  const { user } = useAuth();
    const token = localStorage.getItem('authToken');

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusStyles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };

    const statusLabels = {
      high: 'High Risk',
      medium: 'Medium Risk',
      low: 'Low Risk'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status]}`}>
        <AlertTriangle className="w-3 h-3 mr-1" />
        {statusLabels[status]}
      </span>
    );
  };

  // Due Date Component
  const DueDate = ({ date }) => {
    const dueDate = new Date(date);
    const today = new Date();
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    let colorClass = 'text-green-600';
    if (daysUntilDue <= 0) colorClass = 'text-red-600';
    else if (daysUntilDue <= 7) colorClass = 'text-yellow-600';

    return (
      <div className={`flex items-center ${colorClass}`}>
        <Clock className="w-4 h-4 mr-1" />
        <span className="text-sm">
          {daysUntilDue <= 0 ? 'Overdue' : 
           daysUntilDue === 1 ? 'Due tomorrow' :
           `Due in ${daysUntilDue} days`}
        </span>
      </div>
    );
  };

  // Risk Score Component
  const RiskScore = ({ score }) => {
    const getScoreColor = (score) => {
      if (score >= 16) return 'text-red-600';
      if (score >= 9) return 'text-yellow-600';
      return 'text-green-600';
    };

    return (
      <div className="mt-2 text-right">
        <div className={`text-lg font-bold ${getScoreColor(score)}`}>
          {score}/25
        </div>
        <div className="text-xs text-gray-500">
          Risk Score
        </div>
      </div>
    );
  };
    // Fetch dashboard stats
  // Fetch risks function
  const fetchRisks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams({
        status: filterStatus,
        search: searchTerm,
        approval_level: 'GB'
      });
      
      const response = await fetch(
        `${API_BASE_URL}/risks.php?${queryParams.toString()}`
      );
      const data = await response.json();
      
      if (data.success) {
        setPendingRisks(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch risks');
      }
    } catch (err) {
      console.error('Error fetching risks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats function
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/gb-dashboard-stats.php`);
      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch stats');
      }
    } catch (err) {
      console.error('Error fetching GB stats:', err);
      // Don't set error state for stats to avoid blocking the main functionality
    }
  };

  // Use fetchRisks in useEffect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRisks();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus]);

  // Use fetchStats in useEffect
  useEffect(() => {
    fetchStats();
  }, []);

  // Handle approval/rejection
  // In GBApprovalDashboard.jsx
  const handleApproval = async (riskId, decision, notes) => {
    try {
      setError(null);
      //setIsSubmitting(true);
  
      // Create GB meeting
      const meetingResponse = await fetch(`${API_BASE_URL}/meetings.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'GB',
          title: 'GB Risk Review Meeting',
          date: new Date().toISOString().split('T')[0],
          time: '10:00:00',
          location: 'Board Room',
          status: 'upcoming'
        }),
      });
  
      const meetingData = await meetingResponse.json();
      if (!meetingData.success) {
        throw new Error(meetingData.error || 'Failed to create meeting');
      }
  
      // Assign risk to meeting
      const assignResponse = await fetch(`${API_BASE_URL}/meeting-risks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: meetingData.data.id,
          risk_id: riskId,
          requires_decision: true,
          added_by: user.id
        }),
      });
  
      if (!assignResponse.ok) {
        throw new Error('Failed to assign risk to meeting');
      }
  
      // Record decision
      const decisionResponse = await fetch(`${API_BASE_URL}/meeting-decisions.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          meeting_id: meetingData.data.id,
          risk_id: riskId,
          decision,
          notes,
          decided_by: user.id
        }),
      });
  
      if (!decisionResponse.ok) {
        throw new Error('Failed to record decision');
      }
  
      // Update risk status
      const updateResponse = await fetch(`${API_BASE_URL}/risks.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          risk_id: riskId,
          approval_status: decision === 'approved' ? 'approved' : 'rejected',
          updated_by: user.id
        }),
      });
  
      if (!updateResponse.ok) {
        throw new Error('Failed to update risk status');
      }
  
      // Refresh dashboard
      fetchRisks();
    } catch (err) {
      setError(err.message);
    } finally {
      //setIsSubmitting(false);
    }
  };

  // Render Stats Section
  const renderStats = () => (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-sm text-gray-500">Pending GB Review</div>
        <div className="text-2xl font-bold text-blue-600">{stats.pendingCount}</div>
        <div className="mt-1 text-sm text-gray-500">
          Awaiting governing body decision
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-sm text-gray-500">Next GB Meeting</div>
        {stats.nextMeeting ? (
          <>
            <div className="text-xl font-bold text-gray-900">
              {new Date(stats.nextMeeting.date).toLocaleDateString()}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              {stats.nextMeeting.title}
            </div>
          </>
        ) : (
          <div className="text-xl font-bold text-gray-900">No meeting scheduled</div>
        )}
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-sm text-gray-500">Strategic Risks</div>
        <div className="text-2xl font-bold text-purple-600">{stats.strategicCount}</div>
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className="text-gray-500">Approval Rate:</span>
          <span className="font-medium text-gray-900">
            {((stats.approvalStats.approved / stats.approvalStats.total) * 100 || 0).toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-sm text-gray-500">Financial Impact</div>
        <div className="text-xl font-bold text-gray-900">
          £{(stats.financialImpact / 1000).toFixed(0)}k+
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <div className="text-green-600">
            Approved: {stats.approvalStats.approved}
          </div>
          <div className="text-red-600">
            Rejected: {stats.approvalStats.rejected}
          </div>
        </div>
      </div>
    </div>
  );

  // Render Search and Filters
  const renderSearchAndFilters = () => (
    <div className="mb-6 flex space-x-4">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search risks..."
          className="pl-10 pr-4 py-2 w-full border rounded-md"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="relative">
        <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <select
          className="pl-10 pr-4 py-2 border rounded-md appearance-none bg-white"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="pending">Pending GB Review</option>
          <option value="upcoming">Upcoming Meeting Items</option>
          <option value="approved">GB Approved</option>
          <option value="rejected">GB Rejected</option>
        </select>
      </div>
    </div>
  );
  // Render Risk List
  const renderRisksList = () => (
    <div className="space-y-4">
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : pendingRisks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No risks found pending GB approval
        </div>
      ) : (
        pendingRisks.map(risk => (
          <div key={risk.risk_code} className="bg-white rounded-lg shadow">
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <StatusBadge status={risk.risk_level} />
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">{risk.risk_code}</span>
                      <span className="font-medium">{risk.title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{risk.description}</p>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Category: {risk.category_label} • Owner: {risk.owner_name}
                  </div>
                </div>
                <div className="text-sm text-right">
                  <DueDate date={risk.target_resolution_date} />
                  <RiskScore score={risk.risk_score} />
                </div>
              </div>

              {expandedRisk === risk.risk_code && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    {/* SMT Approval Details */}
                    
                      <div className="space-y-2">
                      {risk.smt_approver && (<>
                        <h4 className="text-sm font-medium text-gray-700">SMT Approval Details</h4>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <div className="text-sm">
                            <div>Approved by: {risk.smt_approver}</div>
                            <div>Date: {new Date(risk.smt_approval_date).toLocaleDateString()}</div>
                            {risk.smt_notes && (
                              <div className="mt-2">
                                <div className="text-gray-500">Notes:</div>
                                <div className="text-gray-700">{risk.smt_notes}</div>
                              </div>
                            )}
                          </div>
                      </div>
                      </>
                      )}
                    

                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Mitigation Strategy</h4>
                        <p className="text-sm text-gray-600">{risk.mitigation_strategy}</p>
                      </div>
                      {risk.contingency_plan && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Contingency Plan</h4>
                          <p className="text-sm text-gray-600">{risk.contingency_plan}</p>
                        </div>
                      )}
                      {risk.notes && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                          <p className="text-sm text-gray-600">{risk.notes}</p>
                        </div>
                      )}
                       {risk.comments && (
                        <>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Comments</h4>
                          {risk.comments.map((comment, index) => (
                            <div key={index} className="bg-gray-50 p-3 rounded mb-2">
                              <div className="flex justify-between mb-1">
                                <span className="font-medium text-gray-800">{comment.full_name}</span>
                                <span className="text-xs text-gray-500">{comment.role}</span>
                              </div>
                              <p className="text-sm text-gray-700">{comment.comment}</p>
                            </div>
                          ))}
                        </>
                      )}

                    </div>

                    {/* GB Review Form */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">GB Review</h4>
                      <textarea
                        placeholder="Add your review comments..."
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        rows="3"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => handleApproval(risk.id, 'rejected',comment)}
                          className="inline-flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </button>
                        <button
                          onClick={() => handleApproval(risk.id, 'approved',comment)}
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setExpandedRisk(expandedRisk === risk.risk_code ? null : risk.risk_code)}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  {expandedRisk === risk.risk_code ? 'Show Less' : 'Review Risk'}
                  <ChevronDown className={`ml-1 w-4 h-4 transform transition-transform ${
                    expandedRisk === risk.risk_code ? 'rotate-180' : ''
                  }`} />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Main Render
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Governing Body Approval Dashboard</h1>
        <div className="text-gray-600">Review and approve strategic risks requiring GB oversight</div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Stats, Search, and Risk List */}
      {renderStats()}
      {renderSearchAndFilters()}
      {renderRisksList()}
    </div>
  );
};

export default GBApprovalDashboard;
